import { type FC, memo, useMemo, useState } from "react";
import { CellWithSubtitle } from "~/features/productDetails/components/DataTables/CellWithSubtitle";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { WeightFormat } from "~/domains/product/components/WeightFormat";
import { ParamComponent, ParamsGrid } from "~/common/components/ParamsGrid";

import Checkbox from "~/common/components/Checkbox";
import OrderTableProduct from "~/features/orders/orderDetails/components/OrderDetails/OrderTableProduct";
import Chip from "~/common/components/Chip/Chip";
import { getAliases } from "~/domains/product/components/ProductAliases";

import { TableBody, TableCell, TableRow } from "~/common/components/Table/TableElements";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useSendOrderFeedback } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/useSendOrderFeedback";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemStaticParams";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { ManualPackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { OrderLineItemElements } from "~/features/orders/common/components/OrderLineItemElements";
import { type OrderData, type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { hasSuccessor } from "~/utils/productUtils";
import {
  getQuoteNotaFiscalData,
  getStatusData,
  sumValues4HeaderOrders,
} from "~/features/orders/orderList/components/OrderLineTable";

import ServiceCasesTable from "~/features/orders/orderDetails/components/ServiceCases/ServiceCasesTable";
import { type CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { Fade } from "~/common/mui";
import OrderSubLineTable from "~/features/orders/common/components/OrderSubLineTable";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";

interface Props {
  rowNum: number;
  data: OrderLineItemData;
  orderData: OrderData;
  selected: boolean;
  selectItem: (index: number) => void;
  showCheckbox: boolean;
  soNumber?: string;
  poNumber?: string;
  serviceCase?: CaseDataObject[];
}

const OrderTableRow: FC<Props> = ({
  data,
  orderData,
  selected,
  selectItem,
  showCheckbox,
  rowNum,
  soNumber,
  poNumber,
  serviceCase,
}) => {
  const { t } = useTranslation();
  const contactSchaefflerPermission = userHasPermission(Permission.ORDER_CONTACT);
  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  const [popups, open, userHasCreatedCase] = useSendOrderFeedback(
    data,
    orderData,
    rowNum,
    soNumber,
    poNumber,
    setSnackbarData,
  );

  const [hasCreatedSubLineCase, setHasCreatedSubLineCase] = useState(false);

  const haveSubEntries = data.subEntries && data?.subEntries?.length > 0;

  const dataCells = useMemo(
    () => (
      <ManualPackageRestrictionContext restrictedPackNo={data.packCode}>
        <TableCell>{data.lineNo}</TableCell>
        <TableCell>
          <OrderTableProduct product={data.product} />
        </TableCell>
        <TableCell>
          {haveSubEntries ? (
            <>{sumValues4HeaderOrders(data, "quantity")}</>
          ) : (
            <CellWithSubtitle
              title={data.confirmedQty ? data.confirmedQty : "-"}
              subtitle={data.qty}
              lightHeader={true}
            />
          )}
        </TableCell>
        <TableCell>
          <CellWithSubtitle
            title={getPackagingLabelAtLineItem({
              t,
              packSize: data.packSize,
              packCode: data.packCode,
              quantity: haveSubEntries ? data.subEntries?.[0].qty ?? data.qty : data.qty,
              productDescriptor: fakeDescriptorFromProduct(data.product),
            })}
            subtitle={translateWarehouseName(t, data.location)}
            lightHeader={true}
          />
          {getAliases(data.product, "15-digits", data.packCode).length > 0 && (
            <ParamComponent
              label={t("product.customerId")}
              value={getAliases(data.product, "15-digits", data.packCode).join(", ")}
            />
          )}
          {isLineLevelCustomerOrderNumberEnabled() && data.customerOrderNumber && (
            <ParamComponent label={t("search.type.customerOrderNumber")} value={data.customerOrderNumber} />
          )}
        </TableCell>
        <TableCell>
          {haveSubEntries ? (
            <>{sumValues4HeaderOrders(data, "deliverDate")}</>
          ) : (
            <CellWithSubtitle
              title={formatShippingOrDeliveryDate(data.deliveryDate, DateFormatType.DATE)}
              subtitle={formatNullableDate(data.requestedDeliveryDate, DateFormatType.DATE)}
              lightHeader={true}
            />
          )}
        </TableCell>
        <TableCell>
          {haveSubEntries ? (
            <CellWithSubtitle
              title={data.subEntries && <WeightFormat weight={data.subEntries[0].weight} />}
              subtitle={t("common.product.perPiece", { count: 1 })}
              lightHeader={true}
            />
          ) : (
            <CellWithSubtitle
              title={<WeightFormat weight={data.weight} />}
              subtitle={t("common.product.perPiece", { count: 1 })}
              lightHeader={true}
            />
          )}
        </TableCell>
        <TableCell>
          <OrderLineItemElements.NetPrice data={data} />
          <PriceReferenceParam className={"mt-2"} product={data.product} lineItem={data} />
        </TableCell>
        <TableCell>
          {haveSubEntries ? (
            <>
              {sumValues4HeaderOrders(data, "netValue")}
              {sumValues4HeaderOrders(data, "surcharge")}
            </>
          ) : (
            <>
              <OrderLineItemElements.TotalPrice data={data} />
              <OrderLineItemElements.Surcharge data={data} className={"mt-2"} />
            </>
          )}
        </TableCell>
        <TableCell>
          {!haveSubEntries ? (
            <>
              <CellWithSubtitle
                title={<Chip key={"chip"}>{t(data.status)}</Chip>}
                lightHeader={true}
                subtitle={
                  <>
                    <ParamsGrid key={`invoice-${data.lineNo}`} params={getStatusData(t, data)} noColumnFlex={true} />
                  </>
                }
              />
              <ParamsGrid
                key={`order-list-quote-nota-fiscal-${data.lineNo}`}
                params={getQuoteNotaFiscalData(t, data)}
                noColumnFlex={true}
              />
            </>
          ) : (
            <div className={"scha__order-list-nota-fiscal"}>
              <ParamsGrid
                key={`order-list-quote-nota-fiscal-${data.lineNo}`}
                params={getQuoteNotaFiscalData(t, data)}
                noColumnFlex={true}
              />
            </div>
          )}
        </TableCell>
      </ManualPackageRestrictionContext>
    ),
    [data, t, open],
  );

  const dataCellsSubEntries = useMemo(() => {
    return (
      <>
        <TableCell />
        <TableCell colSpan={9} className={"scha__order-list__deliveries"}>
          {t("order.deliveries.label")}
        </TableCell>
        {data.subEntries?.map((subEntry, index) => (
          <OrderSubLineTable
            index={index}
            subEntry={subEntry}
            contactSchaefflerPermission={contactSchaefflerPermission}
            data={data}
            orderData={orderData}
            lineItemIndex={rowNum}
            soNumber={soNumber}
            poNumber={poNumber}
            setSnackbarData={setSnackbarData}
            setHasCreatedCaseSpinner={setHasCreatedSubLineCase}
          />
        ))}
      </>
    );
  }, [data]);

  return (
    <>
      <TableBody>
        <TableRow>
          {dataCells}
          {showCheckbox && (
            <TableCell>
              <Checkbox checked={selected} onCheck={() => selectItem(rowNum)} disabled={!data.product.url} />
            </TableCell>
          )}
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell colSpan={9}>
            {contactSchaefflerPermission && (
              <Snackbar
                onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
                open={snackbarData.open}
                autoHideDuration={7000}
                TransitionComponent={Fade}
                transitionDuration={{ exit: 500, appear: 500, enter: 500 }}
                message={snackbarData.message}
                action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
              />
            )}
            {contactSchaefflerPermission && (
              <ServiceCasesTable
                lineNumber={data.lineNo}
                openPopup={open}
                serviceCase={serviceCase}
                orderNumber={soNumber}
                userHasCreatedCase={userHasCreatedCase || hasCreatedSubLineCase}
                setSnackbar={setSnackbarData}
                isOdd={rowNum % 2 === 0}
              />
            )}
          </TableCell>
        </TableRow>
        {haveSubEntries && <>{dataCellsSubEntries}</>}

        {hasSuccessor(data.product) && (
          <TableRow>
            <TableCell />
            <TableCell colSpan={3}>
              <NewVersionMessage className={"my-1"} baseProduct={data.product} />
            </TableCell>
            <TableCell colSpan={6} />
          </TableRow>
        )}

        <TableRow>
          {data.comment && (
            <TableCell colSpan={10}>
              <div className={"mb-1 f-label-color"}>{t("cartPage.table.comment")}</div>
              <div>{data.comment}</div>
            </TableCell>
          )}
        </TableRow>
        {popups}
      </TableBody>
    </>
  );
};

export default memo(OrderTableRow);
